import React, { useCallback, useEffect, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import 'react-toastify/dist/ReactToastify.css';
import useApiCaller from 'hooks/apiCaller';
import debounce from 'lodash.debounce';
import * as Yup from 'yup';

const AssignRemoveDeclarationForm = ({ onTrigger }) => {
  const [declarations, setDeclarations] = useState([]);
  const [inputMode, setInputMode] = useState('individual'); // "individual" | "bulk"

  // Employee autocomplete state (individual mode)
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const apiCaller = useApiCaller();

  useEffect(() => {
    const fetchDeclarations = async () => {
      try {
        const response = await apiCaller.post('/super-user/fetchAllDeclarationsCreatedByAd');
        if (response) {
          setDeclarations(response?.data?.data?.result || []);
        }
      } catch (error) {
        console.error('Error fetching declarations:', error);
      }
    };
    fetchDeclarations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Employee search (same pattern as course mapping / AddEmployeeForm) ────────
  const fetchEmployees = async (search) => {
    if (!search || search.trim().length === 0) {
      setEmployeeOptions([]);
      return;
    }
    setLoadingEmployees(true);
    try {
      const response = await apiCaller.post('/super-user/getEmployee', { searchString: search });
      setEmployeeOptions(response?.data?.data?.employee || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const debouncedFetchEmployees = useCallback(
    debounce((search) => fetchEmployees(search), 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // ── Excel bulk upload helper ──────────────────────────────────────────────────
  const handleFileUpload = (event, setFieldValue) => {
    const file = event.currentTarget.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsBinaryString(file);
    reader.onload = (e) => {
      const data = e.target.result;
      const workbook = XLSX.read(data, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet);
      setFieldValue('empIdsFile', jsonData);
    };
  };

  // ── Validation schemas ────────────────────────────────────────────────────────
  const individualSchema = Yup.object().shape({
    courseName: Yup.string().required('Declaration is required'),
    singleEmpId: Yup.number()
      .typeError('Please select an employee')
      .required('Employee is required'),
    addEmployees: Yup.boolean(),
  });

  const bulkSchema = Yup.object().shape({
    courseName: Yup.string().required('Declaration is required'),
    empIdsFile: Yup.array().min(1, 'Employee file is required'),
    addEmployees: Yup.boolean(),
  });

  const validationSchema = inputMode === 'individual' ? individualSchema : bulkSchema;

  // ── Submit ────────────────────────────────────────────────────────────────────
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    let payload = {
      courseName: values.courseName,
      addEmployees: values.addEmployees ?? false,
    };

    if (inputMode === 'individual') {
      payload.empId = Number(values.singleEmpId);
    } else {
      payload.empIdsFile = values.empIdsFile;
    }

    try {
      const response = await apiCaller.post('/super-user/updateDeclarationEmployees', payload);
      if (response) {
        toast.success(
          values.addEmployees
            ? 'Employee(s) added successfully!'
            : 'Employee(s) removed successfully!',
          { theme: 'colored' }
        );
        resetForm();
        setSelectedEmployee(null);
        setEmployeeOptions([]);
        if (onTrigger) onTrigger(values);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error?.response?.data?.message || 'Failed to update employees.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={{
        courseName: '',
        addEmployees: false,
        removeEmployees: false,
        singleEmpId: '',
        empIdsFile: [],
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ setFieldValue, values, errors, touched, isSubmitting }) => (
        <Form>
          {/* Declaration selector */}
          <Box mt={2}>
            <Typography variant="subtitle1">Select Declaration *</Typography>
            <Field
              as={Select}
              name="courseName"
              fullWidth
              displayEmpty
              error={touched.courseName && Boolean(errors.courseName)}
              onChange={(event) => setFieldValue('courseName', event.target.value)}
            >
              <MenuItem value="" disabled>
                Select a declaration
              </MenuItem>
              {declarations.map((item) => (
                <MenuItem key={item.courseName} value={item.courseName}>
                  {item.courseName}
                </MenuItem>
              ))}
            </Field>
            {touched.courseName && errors.courseName && (
              <Typography color="error" variant="caption">
                {errors.courseName}
              </Typography>
            )}
          </Box>

          {/* Add / Remove checkboxes */}
          <Box mt={2} display="flex" gap={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={values.addEmployees}
                  onChange={(e) => {
                    setFieldValue('addEmployees', e.target.checked);
                    if (e.target.checked) setFieldValue('removeEmployees', false);
                  }}
                />
              }
              label="Add Employees"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={values.removeEmployees}
                  onChange={(e) => {
                    setFieldValue('removeEmployees', e.target.checked);
                    if (e.target.checked) setFieldValue('addEmployees', false);
                  }}
                />
              }
              label="Remove Employees"
            />
          </Box>

          {/* Input mode toggle */}
          <Box mt={2}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#374151' }}>
              Employee Input Method
            </Typography>
            <ToggleButtonGroup
              value={inputMode}
              exclusive
              onChange={(_, newMode) => {
                if (newMode) {
                  setInputMode(newMode);
                  setSelectedEmployee(null);
                  setEmployeeOptions([]);
                  setFieldValue('singleEmpId', '');
                  setFieldValue('empIdsFile', []);
                }
              }}
              size="small"
            >
              <ToggleButton value="individual" sx={{ textTransform: 'none', fontWeight: 600 }}>
                Individual
              </ToggleButton>
              <ToggleButton value="bulk" sx={{ textTransform: 'none', fontWeight: 600 }}>
                Bulk (Excel)
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* ── Individual mode: Autocomplete employee search ── */}
          {inputMode === 'individual' && (
            <Box mt={2}>
              <Typography variant="subtitle1">Search Employee *</Typography>
              <Autocomplete
                size="small"
                value={selectedEmployee}
                options={employeeOptions}
                getOptionLabel={(option) =>
                  option ? `${option.employeeId} - ${option.employeeName}` : ''
                }
                isOptionEqualToValue={(option, value) => option.employeeId === value.employeeId}
                loading={loadingEmployees}
                filterOptions={(x) => x} // disable client-side filtering; server filters
                onChange={(_, newValue) => {
                  setSelectedEmployee(newValue);
                  setFieldValue('singleEmpId', newValue ? newValue.employeeId : '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Search by Employee ID or Name"
                    error={touched.singleEmpId && Boolean(errors.singleEmpId)}
                    helperText={touched.singleEmpId && errors.singleEmpId}
                    onChange={(e) => debouncedFetchEmployees(e.target.value)}
                    sx={{ mt: 1 }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingEmployees ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          )}

          {/* ── Bulk mode: Excel upload ── */}
          {inputMode === 'bulk' && (
            <Box mt={2}>
              <Button
                variant="outlined"
                href="/empUploadDeclarationTemplatexlsx.xlsx"
                download="Employee_Template.xlsx"
                size="small"
                sx={{ mb: 1 }}
              >
                Download Employee Template
              </Button>
              <Typography variant="subtitle1">Upload Employee IDs (Excel/CSV) *</Typography>
              <input
                type="file"
                name="empIdsFile"
                accept=".csv,.xlsx"
                onChange={(event) => handleFileUpload(event, setFieldValue)}
                style={{ display: 'block', marginTop: '8px' }}
              />
              {touched.empIdsFile && errors.empIdsFile && (
                <Typography color="error" variant="caption">
                  {errors.empIdsFile}
                </Typography>
              )}
              {Array.isArray(values.empIdsFile) && values.empIdsFile.length > 0 && (
                <Typography variant="caption" sx={{ color: '#15803d', mt: 0.5, display: 'block' }}>
                  {values.empIdsFile.length} employee(s) loaded from file
                </Typography>
              )}
            </Box>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isSubmitting}
            sx={{
              mt: 3,
              bgcolor: values.removeEmployees ? '#dc2626' : 'primary.main',
              '&:hover': {
                bgcolor: values.removeEmployees ? '#b91c1c' : 'primary.dark',
              },
            }}
          >
            {isSubmitting
              ? 'Updating...'
              : values.removeEmployees
                ? 'Remove Employee(s)'
                : 'Add Employee(s)'}
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default AssignRemoveDeclarationForm;
